/* eslint-disable max-len */
import React, { useMemo } from 'react';
import { Tooltip } from '@mui/material';
import { Info } from 'lucide-react';
import PropTypes from 'prop-types';
import { calculateTimeSummary } from '../../utils/activity';

const softShadow = 'shadow-[0_1px_2px_rgba(14,30,64,0.04)]';

const SEGMENTS = [
  {
    id: 'deep', label: 'Deep Focus', range: '51% - 100%', color: '#213D69',
  },
  {
    id: 'focused', label: 'Focused', range: '21% - 50%', color: '#5A81BC',
  },
  {
    id: 'low', label: 'Low', range: '1% - 20%', color: '#B5BAC4',
  },
  {
    id: 'break', label: 'Work Break', range: '', color: '#F19E41',
  },
];

function engagementColor(value) {
  if (value >= 70) return 'text-shoot-success';
  if (value >= 40) return 'text-[#F19235]';
  return 'text-[#b94f3c]';
}

function ringColorFor(engagement) {
  if (engagement >= 70) return '#6b9f5f';
  if (engagement >= 40) return '#F19235';
  return '#b94f3c';
}

function TeamEngagementScoreCard({ team }) {
  const averageScore = useMemo(() => {
    const totalScore = team?.teamMates?.reduce((score, mate) => {
      const { totalSeconds = 0, activeSeconds = 0 } = calculateTimeSummary(mate.member?.activity);
      return score + Math.round((activeSeconds * 100) / totalSeconds || 0);
    }, 0);
    const teamSize = team.teamMates?.length || 0;
    return teamSize > 0 ? Math.round(totalScore / teamSize) : 0;
  }, [team]);

  const indicatorColor = ringColorFor(averageScore);

  return (
    <div className={`rounded-xl bg-white border border-surface-2 ${softShadow} p-5 flex flex-col h-full`}>
      <p className="text-xs uppercase tracking-wider text-ink-500 font-semibold mb-4 inline-flex items-center gap-1.5">
        Overall Engagement Score
        <Tooltip
          arrow
          placement="right"
          title="A measure of collective involvement and enthusiasm,
            reflecting the team's commitment and productivity.
            Higher scores signify stronger engagement"
        >
          <span className="inline-flex items-center justify-center text-ink-500 cursor-help">
            <Info size={14} />
          </span>
        </Tooltip>
      </p>
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className={`text-5xl font-bold leading-none ${engagementColor(averageScore)}`}>
            {averageScore}
            %
          </p>
          <p className="text-sm text-ink-500 mt-2">Average</p>
        </div>
        <div className="flex flex-col gap-2 text-xs w-56 flex-shrink-0">
          {SEGMENTS.map((s) => (
            <div key={s.id} className="flex items-center gap-2">
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: s.color }}
              />
              <span className="text-ink-900 font-semibold">{s.label}</span>
              {s.range && <span className="text-ink-500">{s.range}</span>}
            </div>
          ))}
        </div>
      </div>
      <div className="mt-auto pt-4 flex items-center gap-2">
        <span className="text-[10px] text-ink-500 font-medium tabular-nums flex-shrink-0">
          0%
        </span>
        <div className="flex-1">
          <div className="relative h-4 mb-1.5">
            <span
              className="absolute bottom-0 text-[11px] font-semibold text-ink-900 tabular-nums whitespace-nowrap"
              style={{
                left: `${averageScore}%`,
                transform: 'translateX(-50%)',
              }}
            >
              {averageScore}
              %
            </span>
          </div>
          <div className="relative h-2">
            <div className="absolute inset-0 bg-surface-1 rounded-full overflow-hidden">
              <Tooltip placement="top" title="Organization average range: 45% - 55%">
                <div
                  className="absolute top-0 bottom-0 bg-[#F19235] cursor-help"
                  style={{ left: '45%', right: '45%' }}
                />
              </Tooltip>
            </div>
            <div
              className="absolute w-0.5 h-4 rounded-full"
              style={{
                left: `${averageScore}%`,
                top: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: indicatorColor,
              }}
            />
          </div>
          <div className="relative h-2 mt-1">
            <div
              className="absolute top-0"
              style={{
                left: `${averageScore}%`,
                transform: 'translateX(-50%)',
              }}
            >
              <svg width="9" height="5" viewBox="0 0 9 5">
                <path d="M4.5 0 L0 5 L9 5 Z" fill={indicatorColor} />
              </svg>
            </div>
          </div>
        </div>
        <span className="text-[10px] text-ink-500 font-medium tabular-nums flex-shrink-0">
          100%
        </span>
      </div>
    </div>
  );
}

TeamEngagementScoreCard.propTypes = {
  team: PropTypes.shape({
    teamMates: PropTypes.arrayOf(
      PropTypes.shape({
        memberId: PropTypes.number.isRequired,
        member: PropTypes.shape({
          user: PropTypes.shape({
            firstName: PropTypes.string.isRequired,
            lastName: PropTypes.string.isRequired,
            profilePicture: PropTypes.string.isRequired,
          }),
        }),
      }),
    ),
  }),
};

export default TeamEngagementScoreCard;
